'use client';

import { Button as RACButton } from 'react-aria-components';
import { useLocalizedMessages } from '../../i18n/localization-provider.js';
import { Button } from '../button/button.js';
import { Popover, PopoverRoot } from '../popover/popover.js';
import { Stack } from '../spacing/stack/stack.js';
import { Link } from '../typography/link.js';
import { Text } from '../typography/text.js';

const ACCOUNT_CLAIM = 'https://claims.cimpress.io/account';
const ACCOUNTS_MANAGEMENT_DASHBOARD_URL = 'https://access.cimpress.io/account-management/accounts';

/** Represents the profile data of the currently logged in user. */
export interface SimpleAuthProfile {
  /** The user's full name. */
  name?: string;
  /** The user's email address. */
  email?: string;
  /** A URL to the user's avatar. */
  picture?: string;
  /** The ID of the account associated with the user. */
  [ACCOUNT_CLAIM]?: string;
}

export interface SimpleAuthToolProps {
  /** The profile data of the currently logged in user. */
  profile?: SimpleAuthProfile;
  /** The name of the account associated with the user. */
  accountName?: string;
  /** A function called when the user requests to log in. */
  onLoginRequested: () => void;
  /** A function called when the user requests to log out. */
  onLogoutRequested: () => void;
}

/** A header tool for simple authentication. */
export function SimpleAuthTool({ profile, accountName, onLoginRequested, onLogoutRequested }: SimpleAuthToolProps) {
  const messages = useLocalizedMessages('simpleAuthTool');

  if (!profile) {
    return (
      <Button size="small" onPress={onLoginRequested}>
        {messages.format('logIn')}
      </Button>
    );
  }

  const { name = messages.format('unknownUser'), email, picture, [ACCOUNT_CLAIM]: account } = profile;

  return (
    <PopoverRoot>
      <Trigger name={name} picture={picture} />
      <Popover
        aria-label={messages.format('profileDetails', { userFullName: name })}
        placement="bottom right"
        UNSAFE_className="cim-simple-auth-tool-popover"
      >
        <Stack gap={16} align="center" margin={12}>
          <Avatar picture={picture} />
          <Text as="p" variant="body-semibold" tone="base">
            {name}
          </Text>
          <Stack gap={4} align="center">
            <Text as="span" variant="medium" tone="base">
              {email}
            </Text>
            {account && accountName && (
              <Link
                href={`${ACCOUNTS_MANAGEMENT_DASHBOARD_URL}/${account}`}
                target="_blank"
                rel="noreferrer"
                variant="medium"
              >
                {accountName}
              </Link>
            )}
          </Stack>
          <Button tone="critical" onPress={onLogoutRequested}>
            {messages.format('logOut')}
          </Button>
        </Stack>
      </Popover>
    </PopoverRoot>
  );
}

SimpleAuthTool.displayName = 'SimpleAuthTool';

function Trigger({ name, picture }: { name: string; picture?: string }) {
  return (
    <RACButton className="cim-simple-auth-tool-trigger" aria-label={name}>
      <Text as="span" variant="medium" tone="base">
        {name}
      </Text>
      <Avatar picture={picture} />
    </RACButton>
  );
}

function Avatar({ picture }: { picture?: string }) {
  return <img src={picture} alt="" className="cim-simple-auth-tool-avatar" />;
}
